import { z } from 'zod';
import { ATTENDANCE_CARD_STATUS_VALUES, ROOM_TAG_STATUS_VALUES } from '@fas/types';
import { nonEmptyTrimmed, paginationQuerySchema, uuidSchema } from './common';

// ── ID cards and NFC room tags (Phase 3G, ADR-062) ─────────────────────────────
/**
 * A card number as a card reader types it: hexadecimal UID or decimal number, with or without
 * spaces, colons or dashes. Case and separators do not matter; enrolment and taps normalize it
 * the same way, so a card is always enrolled with the kind of reader the kiosks use.
 */
export const cardNumberSchema = z
  .string('Tap the card on the reader, or type its number')
  .transform((v) => v.trim().toUpperCase().replace(/[\s:-]/g, ''))
  .pipe(z.string().regex(/^[0-9A-F]{4,32}$/, 'A card number has 4 to 32 digits or letters A–F'));

export const enrolCardSchema = z
  .object({
    userId: uuidSchema,
    label: nonEmptyTrimmed(100, 'Label'),
    number: cardNumberSchema,
  })
  .strict();
export type EnrolCardInput = z.infer<typeof enrolCardSchema>;

export const revokeCardSchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type RevokeCardInput = z.infer<typeof revokeCardSchema>;

export const listCardsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(ATTENDANCE_CARD_STATUS_VALUES).optional(),
  userId: uuidSchema.optional(),
  /** Whose card is this? A found card's number, matched by its hash. */
  number: cardNumberSchema.optional(),
});
export type ListCardsQuery = z.infer<typeof listCardsQuerySchema>;

/** 64-byte ECDSA signature (r‖s), base64url without padding. */
const signatureSchema = z.string().regex(/^[A-Za-z0-9_-]{86}$/, 'Must be a 64-byte base64url signature');

/**
 * A card tapped on a kiosk's reader, signed by the kiosk's key over
 * `fas-display/v1\nTAP\n<displayId>\n<challenge>\n<requestId>\n<number>`.
 */
export const cardTapSchema = z
  .object({
    displayId: uuidSchema,
    challenge: z.string().min(1).max(300),
    signature: signatureSchema,
    /** Idempotency key: a retried tap returns the first result. */
    requestId: uuidSchema,
    number: cardNumberSchema,
  })
  .strict();
export type CardTapInput = z.infer<typeof cardTapSchema>;

/** An NTAG 424 DNA UID: 7 bytes, 14 hex characters. */
export const roomTagUidSchema = z
  .string('Enter the tag’s UID')
  .transform((v) => v.trim().toUpperCase().replace(/[\s:-]/g, ''))
  .pipe(z.string().regex(/^[0-9A-F]{14}$/, 'A tag UID has 14 hexadecimal characters (7 bytes)'));

export const createRoomTagSchema = z
  .object({
    locationId: uuidSchema,
    label: nonEmptyTrimmed(100, 'Label'),
    uid: roomTagUidSchema,
  })
  .strict();
export type CreateRoomTagInput = z.infer<typeof createRoomTagSchema>;

export const revokeRoomTagSchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type RevokeRoomTagInput = z.infer<typeof revokeRoomTagSchema>;

export const listRoomTagsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(ROOM_TAG_STATUS_VALUES).optional(),
  locationId: uuidSchema.optional(),
});
export type ListRoomTagsQuery = z.infer<typeof listRoomTagsQuerySchema>;

/** The parameters a tag mirrors into its URL on every tap (Secure Unique NFC, plain mirroring). */
export const roomTagTapSchema = z
  .object({
    uid: z.string().regex(/^[0-9A-Fa-f]{14}$/, 'Tap the tag again'),
    /** The tag's read counter, 3 bytes as 6 hex characters. */
    ctr: z.string().regex(/^[0-9A-Fa-f]{6}$/, 'Tap the tag again'),
    /** The truncated SUN MAC, 8 bytes as 16 hex characters. */
    cmac: z.string().regex(/^[0-9A-Fa-f]{16}$/, 'Tap the tag again'),
  })
  .strict();
export type RoomTagTapInput = z.infer<typeof roomTagTapSchema>;
